import { Stack, Box, Button, useTheme, Snackbar, Alert, Container, AlertColor } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridRowIdGetter, GridRowModel, GridRowParams, GridSelectionModel } from "@mui/x-data-grid-pro";
import CustomDataGridPro from "components/Common/CustomDataGridPro";
import DataGridColumnGenerator from "components/Common/DataGridColumnGenerator";
import MaintenanceAPI from "components/Maintenance/MaintenanceAPI";
import { AddColumnsForType } from "components/Maintenance/MaintenanceColumns";
import MaintenanceQuoteEdit from "components/Maintenance/MaintenanceQuoteEdit";
import UploadMessageBox from "components/Maintenance/UploadMessageBox";
import { MaintenanceActionEnum, MaintenanceData, useMaintenanceData } from "components/Maintenance/useMaintenanceData";
import ReportSelectionDialog from "components/Reports/ReportSelectionDialog";
import useApplicationInfo from "helpers/context/Application/useApplicationInfo";
import useDefaultActionButtons from "helpers/context/Page/useDefaultActionButtons";
import useMessageBox from "helpers/context/Page/useMessageBox";
import useWait from "helpers/context/Page/useWait";
import useReports, { IReportSelection } from "helpers/context/SelectionValues/useReports";
import useWindowTitle from "helpers/context/Title/useWindowTitle";
import useUserInfo from "helpers/context/User/useUserInfo";
import { ClassificationTypeEnum, CompanyPermissionEnum, RoleEnum, ThemeColorEnum, UserPermissionTypes } from "helpers/enums";
import useCurrencyFormatter from "helpers/hooks/useCurrencyFormatter";
import useFileDownload from "helpers/hooks/useFileDownload";
import useIsMobile from "helpers/hooks/useIsMobile";
import useRowsPerPage from "helpers/hooks/useRowsPerPage";
import { useTranslations } from "@fenetech/translations";
import { IMaintenanceEditQuoteSubmit, IQuoteClassification } from "helpers/interfaces";
import { IMaintenanceQuote } from "models/IMaintenanceQuote";
import { IUploadedOrder } from "models/IUploadedOrder";
import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import SelectStatusType from "./SelectStatusType";
import QuoteAttachmentsAPI from "components/Quotes/QuoteEntry/Attachments/QuoteAttachmentsAPI";
import useQuoteClassifications from "helpers/context/SelectionValues/useQuoteClassifications";
import { IESignatureDocumentStatus } from "models/IESignature";
import RefreshIcon from '@mui/icons-material/Refresh';
import useESignatureMaintenanceHelper from "./useESignatureMaintenanceHelper";

const Maintenance: React.FC<any> = () => {

    const user = useUserInfo();
    const theme = useTheme();
    const isMobile = useIsMobile();
    const tm = useTranslations();
    const cf = useCurrencyFormatter();
    const appInfo = useApplicationInfo();
    const navigate = useNavigate();
    const messageBox = useMessageBox();
    const rowsPerPage = useRowsPerPage("Maintenance");
    const wait = useWait();
    const appClassifications = useQuoteClassifications();

    useWindowTitle(tm.Get("Quote Maintenance"));
    useDefaultActionButtons();

    const [mfgCustomerName, setMfgCustomerName] = React.useState<string | null>(null);
    const [mfgSiteName, setMfgSiteName] = React.useState<string | null>(null);
    const [selectedClassification, setSelectedClassification] = React.useState<IQuoteClassification | null>(null);

    const [pagedData, dataDispatch] = useMaintenanceData(selectedClassification, rowsPerPage.pageSize);
    const [eSignatureStatuses, setESignatureStatuses] = React.useState<IESignatureDocumentStatus[]>([]);

    const [editDialogOKey, setEditDialogOKey] = React.useState<number | null>(null);

    const [selectedRows, setSelectedRows] = React.useState<number[]>([]);
    const [uploadClicked, setUploadClicked] = React.useState<boolean>(false);

    const [isReportSelectionVisible, setIsReportSelectionVisible] = React.useState<boolean>(false);
    const [okeyToPrint, setOkeyToPrint] = React.useState<number | null>(null);
    const [alert, setAlert] = React.useState<[text: string, alertType: AlertColor] | null>(null);

    const { reportSelections, getReportDownloadAPI } = useReports("web", "quote");
    const { reportSelections: reportSelectionsOrder, getReportDownloadAPI: getReportDownloadAPIOrder } = useReports("core", "order");

    const { eSignatureEnabled, onRefreshESignatureStatus, onRefreshAllESignatureStatuses, onRefreshESignatureStatusesForUpload } = useESignatureMaintenanceHelper({eSignatureStatuses, setESignatureStatuses, pagedData, setAlert});

    const canCreateModifyQuotes = useMemo(() => {
        return user.HasPermission(UserPermissionTypes.CreateModifyQuotes);
    }, [user]);

    const canDeleteQuotes = useMemo(() => {
        return user.HasPermission(UserPermissionTypes.DeleteQuotes);
    }, [user]);

    const handleChangeMfgCustomerName = (customerName: string | null, siteName: string | null) => {
        setMfgCustomerName(customerName);
        setMfgSiteName(siteName);
    };

    const fileDownload = useFileDownload({
        preDownload: () =>  wait.Show(true),
        postDownload: () =>  wait.Show(false),
        onError: () => handlePrintError(),
    });

    const handlePrintError = () => {
        wait.Show(false);
        setAlert([tm.Get("An unknown error has occurred."), ThemeColorEnum.Error]);
    };

    const isUploadClassification = useMemo(() => {
        return pagedData?.data.some((p: IMaintenanceQuote | IUploadedOrder) => (p as IUploadedOrder).oKeyQuote);
    }, [pagedData]);

    const submitMessage = tm.Get("You are about to submit the selected quotes. You are responsible for the content of each quote. Do you want to proceed?");
    const multiDeleteMessage = tm.Get("Are you sure you want to delete the selected quote(s)?");

    const getRowId: GridRowIdGetter = useCallback((row: GridRowModel) => {
        return row["oKey"] + "|" + Date.now().toString();
    }, []);

    const ShouldIncludeQuote = useCallback((quote: any) => {
        if (mfgCustomerName) {

            //Default to like search on the customer name
            let filterValue = mfgCustomerName;

            if (mfgSiteName) {
                if (mfgSiteName === mfgCustomerName) {
                    //Match the exact string for the 'default' site
                    filterValue = `^${mfgCustomerName}$`
                } else {
                    //Match the exact customer site name 
                    filterValue = `${mfgCustomerName}\\[${mfgSiteName}\\]`;
                }
            }

            const quoteValue = quote["mfgSiteName"];
            let compare = new RegExp(filterValue, "i"); //i for ignore case
            return compare.test(quoteValue);
        }

        return true;

    }, [mfgCustomerName, mfgSiteName]);

    const uploadVisible = useMemo(() => {
        return selectedClassification !== null && (selectedClassification.classificationType === ClassificationTypeEnum.Available || selectedClassification.classificationType === ClassificationTypeEnum.AvailableBeforeUpload) &&
            user.HasRole(RoleEnum.Dealer)
    }, [selectedClassification, user]);

    const submitVisible = useMemo(() => {
        return selectedClassification !== null && (selectedClassification.classificationType === ClassificationTypeEnum.Available || selectedClassification.classificationType === ClassificationTypeEnum.AvailableBeforeUpload) &&
            user.HasRole(RoleEnum.Contractor);
    }, [selectedClassification, user]);

    const multiDeleteVisible = useMemo(() => {
        return selectedClassification !== null && selectedClassification.classificationType === ClassificationTypeEnum.Expired
    }, [selectedClassification]);

    const checkboxSelectionEnabled = useMemo(() => {
        return uploadVisible || submitVisible || multiDeleteVisible;
    }, [uploadVisible, submitVisible, multiDeleteVisible]);

    const updateQuotesWithESignatureInfo = useCallback((quoteData: any[]) => {
        eSignatureStatuses.forEach(s => {
            const index = quoteData.findIndex(q => q["oKey"] === s.oKey);
            if (index !== -1) {
                quoteData[index]["ESignatureStatus"] = s.status;
                quoteData[index]["DocumentReferenceID"] = s.documentReferenceID;
            }
        });

        return quoteData;
    }, [eSignatureStatuses]);

    const quotes = React.useMemo(() => {
        if (pagedData) {
            //Casting as any is required to work around 
            // https://github.com/microsoft/TypeScript/issues/44373
            const anyData = pagedData.data as any[];
            const filtered = anyData.filter(q => ShouldIncludeQuote(q));
            if (eSignatureStatuses && eSignatureStatuses.length > 0) {
                return updateQuotesWithESignatureInfo(filtered);
            }
            return filtered;
        } else {
            return null;
        }
    }, [pagedData, ShouldIncludeQuote, updateQuotesWithESignatureInfo, eSignatureStatuses]);

    const copyQuoteHandler = useCallback(async (okey: number) => {
        wait.Show(true);

        try {
            const newOkey = await MaintenanceAPI.CopyQuote(okey);
            await QuoteAttachmentsAPI.CopyAllQuoteAttachmentsAsync(okey, newOkey);
            navigate(`/quotes/?oKey=${newOkey}`);
        }
        finally {
            wait.Show(false);
        }
    }, [wait, navigate]);

    const deleteQuoteHandler = useCallback((okey: number) => {
        wait.Show(true);
        MaintenanceAPI.DeleteQuote(okey).then(() => {
            //remove this guy from quotes 
            if (pagedData && selectedClassification) {
                const anyData = pagedData.data as any[];
                const newData = anyData.filter((row: IUploadedOrder | IMaintenanceQuote) => row.oKey !== okey) as IMaintenanceQuote[];
                const newPagedData: MaintenanceData = {
                    data: newData,
                    totalRecords: newData.length
                };
                dataDispatch({ type: MaintenanceActionEnum.AddOrUpdateData, value: [selectedClassification.classificationID, newPagedData] });
            }
        }).finally(() => {
            wait.Show(false);
        });
    }, [wait, pagedData, selectedClassification, dataDispatch]);

    const editQuoteHandler = useCallback((okey: number) => {
        setEditDialogOKey(okey);
    }, []);

    const dialogCloseHandler = useCallback((newState: IMaintenanceEditQuoteSubmit | null) => {

        if (newState) {
            // Update the current record with new values
            if (pagedData && selectedClassification) {
                const anyData = pagedData.data as any[];

                //Add new distach to update single row and look up 
                const row = anyData.find((d) => d.oKey === newState.okey);

                if (row) {

                    const rowIndex = anyData.indexOf(row);

                    const quote: IMaintenanceQuote = { ...row } as IMaintenanceQuote;
                    quote.poNumber = newState.poNumber;
                    quote.customerRef = newState.customerRef;
                    quote.requestDate = newState.requestDate?.toISOString();

                    const newData = anyData.slice(0);
                    newData[rowIndex] = quote;

                    const newPagedData: MaintenanceData = {
                        data: newData,
                        totalRecords: newData.length
                    };
                    dataDispatch({ type: MaintenanceActionEnum.AddOrUpdateData, value: [selectedClassification.classificationID, newPagedData] });

                }

            }

        }

        setEditDialogOKey(null);

    }, [pagedData, selectedClassification, setEditDialogOKey, dataDispatch]);

    const printHandler = useCallback((okey: number) => {
        setOkeyToPrint(okey);
        setIsReportSelectionVisible(true);
    }, []);

    const selectionModelChangeHandler = useCallback((selectedRows: GridSelectionModel) => {
        var newSet: number[] = [];
        selectedRows.forEach((row) => newSet.push(Number(row.valueOf().toString().split("|")[0])));
        newSet.sort();
        setSelectedRows(newSet);
    }, []);

    const isQuoteSelectable = useCallback((quote: GridRowParams) => {
        if (submitVisible) {
            return true;
        } else if (uploadVisible) {
            if (user.HasPermission(UserPermissionTypes.UploadQuotesWithExceptions) && user.HasCompanyPermission(CompanyPermissionEnum.UploadQuotesWithExceptions)) {
                return true;
            } else {
                return !quote.row.hasExceptions;
            }
        } else if (multiDeleteVisible) {
            return true;
        } else {
            return false;
        }
    }, [submitVisible, uploadVisible, multiDeleteVisible, user]);

    const handleUploadClick = useCallback(() => {
        if (selectedRows.length > 0) {
            onRefreshESignatureStatusesForUpload(selectedRows).then((loginRequired: boolean) => {
                if (!loginRequired) {
                    setUploadClicked(true);
                }
            });
        } else {
            messageBox.Show({ message: tm.Get("No quotes were selected."), title: tm.Get("Quote Maintenance") });
        }
    }, [selectedRows, messageBox, tm, onRefreshESignatureStatusesForUpload]);

    const handleUpload = useCallback(() => {
        setUploadClicked(false);
        wait.Show(true);
        MaintenanceAPI.UploadQuotes(selectedRows).then((quotes) => {
            navigate("/maintenance/confirmation", { state: quotes });
        }).finally(() => {
            wait.Show(false);
        });
    }, [wait, selectedRows, navigate]);

    const handleSubmitClick = useCallback(() => {
        if (selectedRows.length > 0) {
            messageBox.Show({
                message: submitMessage, title: tm.Get("Quote Maintenance"), yesNoPrompt: true,
                callback: (dialogResult?) => {
                    if (dialogResult) {
                        wait.Show(true);
                        MaintenanceAPI.SubmitQuotes(selectedRows).then((quotes) => {
                            navigate("/maintenance/confirmation", { state: quotes });
                        });
                    }
                }
            });
        } else {
            messageBox.Show({ message: tm.Get("No quotes were selected."), title: tm.Get("Quote Maintenance") });
        }
    }, [wait, selectedRows, messageBox, submitMessage, tm, navigate]);

    const handleMultiDeleteClick = useCallback(() => {
        if (selectedRows.length > 0) {
            messageBox.Show({
                message: multiDeleteMessage, title: tm.Get("Quote Maintenance"), yesNoPrompt: true,
                callback: (dialogResult?) => {
                    if (dialogResult) {
                        wait.Show(true);
                        MaintenanceAPI.MultiDeleteQuotes(selectedRows).then(() => {
                            //remove these guys from quotes 
                            if (pagedData && selectedClassification) {
                                let anyData = pagedData.data as any[];
                                selectedRows.forEach((okey: number) => anyData = anyData.filter((row: IUploadedOrder | IMaintenanceQuote) => row.oKey !== okey));
                                const newPagedData: MaintenanceData = {
                                    data: anyData as IMaintenanceQuote[],
                                    totalRecords: anyData.length
                                };
                                dataDispatch({ type: MaintenanceActionEnum.AddOrUpdateData, value: [selectedClassification.classificationID, newPagedData] });
                            }
                        }).finally(() => {
                            wait.Show(false);
                        });
                    }
                }
            });
        } else {
            messageBox.Show({ message: tm.Get("No quotes were selected."), title: tm.Get("Quote Maintenance") });
        }
    }, [wait, selectedRows, pagedData, selectedClassification, messageBox, multiDeleteMessage, tm, dataDispatch]);

    const columns = useMemo(() => {
        const generator = new DataGridColumnGenerator(tm, quotes, theme, isMobile, cf);
        AddColumnsForType(tm, selectedClassification?.classificationType, generator, appInfo, user, quotes, printHandler,
            canCreateModifyQuotes ? copyQuoteHandler : null,
            canDeleteQuotes ? deleteQuoteHandler : null,
            canCreateModifyQuotes ? editQuoteHandler : null,
            onRefreshESignatureStatus);

        if (checkboxSelectionEnabled) {
            return [
                {
                    ...GRID_CHECKBOX_SELECTION_COL_DEF,
                    hideable: false
                },
                ...generator.GetColumns()
            ]
        } else {
            return generator.GetColumns();
        }
    }, [tm, quotes, theme, isMobile, cf, selectedClassification?.classificationType, appInfo, user, checkboxSelectionEnabled,
        printHandler, copyQuoteHandler, deleteQuoteHandler, editQuoteHandler, canCreateModifyQuotes, canDeleteQuotes, onRefreshESignatureStatus]);

    const showRequestDate: boolean = React.useMemo(() => {
        if (pagedData && pagedData.data.length > 0) {
            const firstRecord = pagedData.data[0];
            return firstRecord.hasOwnProperty("requestDate"); //FUTURE: there doesn't seem to be a nameof operator in typescript.  Investigate a way to reference IMaintetanceEditQuote.requestDate instead of using a string.
        }

        return false;

    }, [pagedData]);

    const onReportSelected = useCallback((selectedReport: IReportSelection): void => {
        setIsReportSelectionVisible(false);
        if (okeyToPrint) {
            if (isUploadClassification) {
                fileDownload.download({ apiCall: () => getReportDownloadAPIOrder(okeyToPrint, selectedReport.reportID, "PDF") });
            } else {
                fileDownload.download({ apiCall: () => getReportDownloadAPI(okeyToPrint, selectedReport.reportID, "PDF") });
            }
        }
    }, [okeyToPrint, isUploadClassification, fileDownload, getReportDownloadAPI, getReportDownloadAPIOrder]);

    const handleAlertClose = useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') 
            return;
        setAlert(null);
    }, []);

    const allClassifications = React.useMemo<IQuoteClassification[]>(() => {

        const csr = user.HasRole(RoleEnum.CSR);
        const dealer = user.HasRole(RoleEnum.Dealer);
        const contractor = user.HasRole(RoleEnum.Contractor);
        const classifications: IQuoteClassification[] = [];

        if (appClassifications) {

            if (csr) {
                classifications.push({
                    classificationID: ClassificationTypeEnum.Recent,
                    classificationType: ClassificationTypeEnum.Recent,
                    description: "Recent"
                })
            }

            const onHoldBeforeUpload = appClassifications?.classifications.filter(c => c.classificationType === ClassificationTypeEnum.OnHoldBeforeUpload);
            const availBeforeUpload = appClassifications?.classifications.filter(c => c.classificationType === ClassificationTypeEnum.AvailableBeforeUpload);
            const afterUpload = appClassifications?.classifications.filter(c => c.classificationType === ClassificationTypeEnum.AfterUpload);

            if (availBeforeUpload) classifications.push(...availBeforeUpload);
            if (onHoldBeforeUpload) classifications.push(...onHoldBeforeUpload);

            if (dealer || csr) {
                classifications.push({
                    classificationType: ClassificationTypeEnum.Uploaded,
                    classificationID: ClassificationTypeEnum.Uploaded,
                    description: "Uploaded"
                });

            } else if (contractor) {
                classifications.push({
                    classificationType: ClassificationTypeEnum.Submitted,
                    classificationID: ClassificationTypeEnum.Submitted,
                    description: "Submitted"
                });

            }

            if (dealer || csr) {
                if (afterUpload) classifications.push(...afterUpload);
            }

            classifications.push({
                classificationType: ClassificationTypeEnum.Expired,
                classificationID: ClassificationTypeEnum.Expired,
                description: "Expired"
            })
        }

        return classifications;
    }, [user, appClassifications]);

    return <Container maxWidth="xl" disableGutters>

        <ReportSelectionDialog
            title={tm.Get("Print")}
            dialogVisible={isReportSelectionVisible}
            reports={isUploadClassification ? reportSelectionsOrder : reportSelections}
            onSubmit={onReportSelected}
            onCancel={() => setIsReportSelectionVisible(false)}
        />

        <Snackbar open={alert !== null} autoHideDuration={5000} onClose={handleAlertClose} >
            <Alert onClose={handleAlertClose} severity={alert ? alert[1] : "info"} variant='filled' sx={{width: '100%', fontWeight: 'bold'}}>{alert ? alert[0] : ""}</Alert>
        </Snackbar>

        {/* Hidden link to host file downloads */}
        <a hidden ref={fileDownload.ref} />

        <Stack direction="column" m={1} spacing={1}>
            <Stack direction="row" justifyContent="flex-start" spacing={1}>
                <SelectStatusType
                    handleChangeMfgCustomerName={handleChangeMfgCustomerName}
                    setSelectedClassification={setSelectedClassification}
                    selectedClassification={selectedClassification}
                    allClassifications={allClassifications}
                />
                {eSignatureEnabled &&
                    <Button onClick={onRefreshAllESignatureStatuses}>
                        <RefreshIcon sx={{mr: 1}} /> {tm.Get("E-Signature Status")}
                    </Button>
                }
            </Stack>

            {allClassifications.map(c => <React.Fragment key={c.classificationID.toString()}>
                { // This is ugly, but it's the only way I could get the app to not crash from column manager when switching modes. 
                  // The map makes it actually render a whole new component instance each time thanks to the key, 
                  //    something about the way this screen works was making it refresh in a half state otherwise
                    c.classificationType === selectedClassification?.classificationType && 
                        <CustomDataGridPro
                            onSelectionModelChange={selectionModelChangeHandler}
                            isRowSelectable={isQuoteSelectable}
                            getRowId={getRowId}
                            columns={columns}
                            rows={quotes}
                            checkboxSelection={checkboxSelectionEnabled}
                            tableName="Maintenance"
                            tableSubName={ClassificationTypeEnum[c.classificationType] + "_" + (isMobile ? "Mobile" : "")}
                            hideByDefaultColumns={[
                                "billingName"
                            ]}
                        />
                }
            </React.Fragment>)}

            {uploadVisible &&
                <Box display="flex" alignItems="center" justifyContent="center">
                    <Button color="secondary" onClick={handleUploadClick} variant="contained">{tm.Get("Upload Selected Quotes To Manufacturer")}</Button>
                </Box>
            }

            {submitVisible &&
                <Box display="flex" alignItems="center" justifyContent="center">
                    <Button color="secondary" onClick={handleSubmitClick} variant="contained">{tm.Get("Submit Selected Quotes")}</Button>
                </Box>
            }

            {multiDeleteVisible &&
                <Box display="flex" alignItems="center" justifyContent="center">
                    <Button color="secondary" onClick={handleMultiDeleteClick} variant="contained">{tm.GetWithParams("Delete {0}", tm.Get("Quotes"))}</Button>
                </Box>
            }
        </Stack>

        {editDialogOKey &&
            <MaintenanceQuoteEdit
                okey={editDialogOKey ?? 0}
                open={(editDialogOKey != null)}
                close={dialogCloseHandler}
                showRequestDate={showRequestDate}
            />
        }

        {uploadClicked &&
            <UploadMessageBox
                open={uploadClicked}
                close={() => setUploadClicked(false)}
                handleUpload={handleUpload}
            />
        }

    </Container>;

}



export default Maintenance;
